import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { t } from '@lingui/macro';
import ReactPlayer from 'react-player';
import Icon from '../components/Icons';
import { useIntl } from '../components/IntlProvider';
import { ensureURLProtocol, getMediaType /* , testImage */ } from '../utils';
import styles from './Carousel.module.scss';
import { AnalyticsEvents, AnalyticsScores, EmbedTypes, networks } from '../consts';
import Caption from '../components/Caption';

const Slide = ({
  index,
  id,
  media,
  service,
  caption,
  time,
  customLink = {},
  color,
  disableHashtagLinks,
  onMediaError,
  username,
  altText,
  disableDates,
  carouselHideCaptions,
  type,
  onVisibleChange,
  carouselHideSocialIcon,
}) => {
  const captionLength = customLink.linkUrl ? 150 : 300;
  const { formatDistanceLocale } = useIntl();
  const dateText = `${formatDistanceLocale(new Date(time || 0), new Date())} ${t`ago`}`;
  const ref = useRef();
  const handleCustomLinkClick = () => {
    giggAnalytics.logEvent(AnalyticsEvents.CTA_CLICK, {
      boardId: __gigg.accountId,
      boardType: EmbedTypes.CAROUSEL,
      folderId: __gigg.folderId,
      postIndex: index,
      postId: id,
      postType: service,
      mediaType: getMediaType(media),
      isCarousel: media?.length > 1,
      CTA_URL: customLink.linkUrl,
      scoreWeight: AnalyticsScores.CTA_CLICK,
      firstEvent: {
        _id: window.giggFirstEvent?._id,
        createdDate: window.giggFirstEvent?.createdDate,
      },
      inViewEvent: {
        _id: window.latestViewEvent?._id,
        createdDate: window.latestViewEvent?.createdDate,
      },
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      const { boundingClientRect, rootBounds } = entry;
      const isVisible =
        boundingClientRect.x + boundingClientRect.width >= rootBounds.left &&
        boundingClientRect.x < rootBounds.width;

      onVisibleChange(ref.current, isVisible);
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      observer.unobserve(ref.current);
    };
  }, []);

  const handleVideoError = (err) => {
    onMediaError(id, err);
  };

  return (
    <span
      className={styles.carouselSlideWrapper}
      aria-label={caption}
      role="button"
      title={caption}
      tabIndex={0}
      inert
      ref={ref}
    >
      <div id={`gigg-slide-${index}`} className={classNames(styles.carouselSlideAriaCaption)} inert>
        {!carouselHideCaptions && { caption }}
      </div>
      <div className={classNames(styles.carouselSlideHeader, 'gigg-carousel__slide-header')}>
        <div className={classNames(styles.carouselSlideUsername)}>{username && `@${username}`}</div>
        {!carouselHideSocialIcon && <Icon icon={service} />}
      </div>
      <div className={classNames(styles.carouselSlideBody, 'gigg-carousel__slide-body')}>
        {!carouselHideSocialIcon && (
          <div className={classNames(styles.carouselSlideHeader, 'gigg-carousel__slide-header')}>
            <Icon icon={service} />
          </div>
        )}
        <div className={classNames(styles.carouselSlideBody, 'gigg-carousel__slide-body')}>
          <div
            className={classNames(styles.carouselSlideCaption, 'gigg-carousel__slide-caption')}
            inert
          >
            {!carouselHideCaptions && (
              <Caption
                service={service}
                color={color}
                maxLength={captionLength}
                disableLinks={disableHashtagLinks}
              >
                {caption}
              </Caption>
            )}
          </div>
          {customLink.linkText && customLink.linkUrl && (
            <div
              className={classNames(
                styles.carouselSlideBtnContainer,
                'gigg-carousel__slide-btn-container'
              )}
            >
              <a
                target="_blank"
                rel="noopener noreferrer"
                className={classNames(styles.carouselSlideBtn, 'gigg-carousel__slide-btn')}
                href={ensureURLProtocol(customLink.linkUrl)}
                onClick={handleCustomLinkClick}
              >
                {customLink.linkText}
              </a>
            </div>
          )}
        </div>
      </div>
      <div className={classNames(styles.carouselSlideImage, 'gigg-carousel__slide-image')}>
        {media[0].video ? (
          <ReactPlayer
            url={media[0].video}
            loop
            volume={0}
            muted
            playing
            playsinline
            width="100%"
            height="100%"
            className={classNames(styles.carouselSlideVideo, 'gigg-carousel__video-player')}
            onError={handleVideoError}
          />
        ) : (
          <img src={media[0].medium} alt={altText} loading="lazy" />
        )}
      </div>
      <div className={classNames(styles.carouselSlideFooter, 'gigg-carousel__slide-footer')}>
        {!disableDates && type !== networks.CUSTOM && (
          <div className={classNames(styles.carouselSlideDate, 'gigg-carousel__slide-date')}>
            {dateText}
          </div>
        )}
      </div>
    </span>
  );
};

export default Slide;
